// ============================================================
// GitRemotes.jsx
//
// A branded, responsive course content page for the
// "Working with Git Remotes" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Remote linking, synchronization (Push/Pull),
//           and SSH vs HTTPS protocol comparisons.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import PublicIcon             from "@mui/icons-material/Public";
import CloudUploadIcon        from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon      from "@mui/icons-material/CloudDownload";
import LinkIcon               from "@mui/icons-material/Link";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import SecurityIcon           from "@mui/icons-material/Security";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitRemotes() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<PublicIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Collaboration & Sync"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Working with <span>Git Remotes</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Break out of the local silo. Remotes allow you to host your code on platforms like GitHub, enabling collaboration with teams across the globe and ensuring your project is backed up safely in the cloud.
                    </Typography>
                </Box>

                {/* 1. LINKING REMOTES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LinkIcon />} label="1. Establishing the Link" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A remote is simply a URL alias. By convention, the primary remote is named <code>origin</code>.
                    </Typography>
                    <CodeBlock
                        title="Remote Management"
                        code={`# Add a new remote\ngit remote add origin https://github.com/user/project.git\n\n# List all linked remotes\ngit remote -v\n\n# Change an existing remote URL\ngit remote set-url origin git@github.com:user/project.git`}
                    />
                </Box>

                {/* 2. SYNCING DATA */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncAltIcon sx={{ color: BRAND.gold }} />} label="2. Pushing & Pulling" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <CloudUploadIcon sx={{ color: BRAND.gold }} />
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700 }}>Pushing Changes</Typography>
                                </Box>
                                <Typography sx={{ color: BRAND.white, fontSize: 12, mb: 2 }}>Uploads your local commits to the server. The -u flag links your local branch to the remote one.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git push -u origin main</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <CloudDownloadIcon sx={{ color: BRAND.gold }} />
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700 }}>Pulling Changes</Typography>
                                </Box>
                                <Typography sx={{ color: BRAND.white, fontSize: 12, mb: 2 }}>Fetches latest work from the team and merges it into your current branch automatically.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git pull origin main</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. PROTOCOLS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="3. SSH vs HTTPS Protocols" />
                    <Grid container spacing={1.5}>
                        {[
                            { title: "HTTPS", desc: "Firewall friendly. Requires username/password or Personal Access Tokens for every push." },
                            { title: "SSH", desc: "Uses public/private key encryption. Once set up, no passwords are required for operations." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, mb: 1 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* REMOTE BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Remote Collaboration Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always 'git pull' before starting new work to minimize merge conflicts.",
                            "Push frequently so your team stays updated and your code is backed up.",
                            "Verify your remote URL before pushing using 'git remote -v'.",
                            "Use SSH for a smoother developer experience without password prompts.",
                            "Never force push (git push -f) to shared branches like 'main'.",
                            "Delete local tracking branches if the remote branch has been merged and deleted."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Confused about the difference between <code>git fetch</code> and <code>git pull</code>? Fetch only downloads the data; pull downloads <strong>and</strong> merges it immediately. Fetch is safer for reviewing changes before committing to a merge!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}

// Dummy Icon for the title logic
function SyncAltIcon(props) {
    return (
        <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 12l4-4-4-4v3H3v2h15v3zM6 12l-4 4 4 4v-3h15v-2H6v-3z" />
        </svg>
    );
}
